/* storybook-check-ignore */
import { Flex, Text, Button, Box, Icon } from '@opendoor/bricks/core';
import {
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useMemo,
  UIEventHandler,
  useEffect,
} from 'react';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import LightningIcon from 'components/exclusives/icons/LightningIcon';
import {
  ExclusiveTourRequestTimeSlot,
  isPreferredExclusiveTourRequestTimeSlot,
  arrayIncludeSlot,
  DEFAULT_TIMEZONE,
} from './util';
import difference from 'lodash/difference';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { motion, AnimatePresence } from 'framer-motion';
import omit from 'lodash/omit';
import RequestATourSupportSection from './RequestATourSupportSection';
import { Listing } from 'declarations/exclusives/listing';
import { useRequestATour } from './useRequestATour';

export default function ReserveATourSelectSlots(props: {
  selectedSlots: ExclusiveTourRequestTimeSlot[];
  setSelectedSlots: Dispatch<SetStateAction<ExclusiveTourRequestTimeSlot[] | undefined>>;
  instantSlots: ExclusiveTourRequestTimeSlot[];
  slots: ExclusiveTourRequestTimeSlot[];
  device: ReturnType<typeof useDevice>;
  onRequestTimeForTourClick: () => void;
  isInstantSlot: ReturnType<typeof useRequestATour>['isInstantSlot'];
  listing: Listing;
}) {
  const timezone = props.listing.timezone || DEFAULT_TIMEZONE;
  const nowPlusOneDay = moment.tz(timezone).add(1, 'day');
  const { nSelectedSlots, ...infoProps } = useMemo(() => {
    const nSelectedSlots = props.selectedSlots.length;
    const hasInstantSpotSelected = props.selectedSlots.some((s) => props.instantSlots.includes(s));
    return {
      nSelectedSlots,
      hasInstantSpotSelected,
      hasNoPreferredOrInstantSlotsSelected:
        !hasInstantSpotSelected &&
        !!nSelectedSlots &&
        !props.selectedSlots.some((s) =>
          isPreferredExclusiveTourRequestTimeSlot(s, props.listing.timezone),
        ),
    };
  }, [props.selectedSlots, props.instantSlots]);
  const canRequestTimeTour =
    (nSelectedSlots >= 3 && nSelectedSlots <= 9) || infoProps.hasInstantSpotSelected;
  const gridRef = useRef<HTMLDivElement>(null);
  const [disableScroll, setDisableScroll] = useState({ left: true, right: false });
  useEffect(() => {
    // Initially scroll the selected slot into the view.
    const slot = props.selectedSlots[0];
    if (slot) {
      const id = props.slots.findIndex((s) => s == slot);
      if (id >= 0) {
        document.getElementById(`slot${id}`)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);
  const onGridScroll = useMemo<UIEventHandler<HTMLDivElement>>(() => {
    const callback: UIEventHandler<HTMLDivElement> = () => {
      if (gridRef.current) {
        const { scrollLeft, clientWidth, scrollWidth } = gridRef.current;
        setDisableScroll({
          left: scrollLeft == 0,
          right: scrollLeft + clientWidth == scrollWidth,
        });
      }
    };
    return debounce(callback, 100);
  }, [setDisableScroll]);
  function scrollGrid(to: 'left' | 'right') {
    if (gridRef.current) {
      gridRef.current.scrollBy({
        left: (to === 'left' ? -1 : 1) * gridRef.current.clientWidth,
        behavior: 'smooth',
      });
    }
  }
  const onSpotClick = (slot: ExclusiveTourRequestTimeSlot) =>
    props.setSelectedSlots((selectedSlots) => {
      if (selectedSlots && arrayIncludeSlot(selectedSlots, slot)) {
        return difference(selectedSlots, [slot]);
      }
      return [...(selectedSlots || []), slot];
    });

  return (
    <>
      <Text as="div" fontSize="s2">
        <Text fontWeight="bold" fontSize="s4">
          Request a new time for self tour <br />
        </Text>
        Selecting{' '}
        <Text
          as="span"
          textColor="transparent"
          background="linear-gradient(56.35deg, #082DE6 36.59%, #0D76D6 103.23%)"
          backgroundClip="text"
        >
          preferred
        </Text>{' '}
        times increase the likelihood of the seller confirming.
      </Text>
      <Flex flexDirection="column">
        <Flex width="100%" flexDirection="row" justifyContent="space-between">
          <Button
            disabled={disableScroll.left}
            variant="icon"
            aria-label={null}
            analyticsName=""
            onClick={() => scrollGrid('left')}
          >
            <Icon name="arrow-left" size={16} />
          </Button>
          <Text textAlign="center" fontWeight="semibold" color="neutrals100" flex={1}>
            {props.selectedSlots.length} selected
          </Text>
          <Button
            disabled={disableScroll.right}
            variant="icon"
            aria-label={null}
            analyticsName=""
            onClick={() => scrollGrid('right')}
          >
            <Icon name="arrow-right" size={16} />
          </Button>
        </Flex>
      </Flex>
      <Box
        display="grid"
        gridTemplateColumns="repeat(10, minmax(120px, 1fr))"
        gridTemplateRows="repeat(7, minmax(60px, 1fr))"
        gridAutoFlow="column"
        overflow="auto"
        gridGap="0.25rem"
        gridColumnGap="0.5rem"
        paddingBottom="1rem"
        onScroll={onGridScroll}
        ref={gridRef}
      >
        {props.slots.map((slot, i) => {
          const startTime = moment(slot.start_time).tz(timezone);
          return (
            <>
              {i % 6 == 0 && (
                <Text textAlign="center" color="neutrals90" as="div">
                  <Text fontWeight="bold" textAlign="center" color="neutrals100">
                    {startTime.format('ddd')}
                  </Text>
                  {startTime.format('MMM DD')}
                </Text>
              )}
              <Spot
                id={`slot${i}`}
                isDisabled={nowPlusOneDay > startTime}
                slot={slot}
                isSelected={arrayIncludeSlot(props.selectedSlots, slot)}
                onClick={onSpotClick}
                isInstant={props.isInstantSlot(slot)}
                timezone={props.listing.timezone}
              />
            </>
          );
        })}
      </Box>
      <RequestATourSupportSection />
      <Flex width="100%" position="sticky" bottom="0" paddingTop="1rem">
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="center"
          background="neutrals0"
          flexDirection="column"
          gridGap="1rem"
          position="relative"
        >
          <Flex
            position="absolute"
            height="2rem"
            top="-1rem"
            bg="neutrals0"
            zIndex={-1}
            width={[
              'calc(100% + 2 * var(--space-7))',
              null,
              null,
              null,
              'calc(100% + 2 * var(--space-11))',
            ]}
            borderTop="1px solid var(--colors-neutrals40)"
            boxShadow="0px 10px 22px rgba(29, 71, 123, 0.08), 0px 2px 12px rgba(29, 71, 123, 0.08)"
          />
          <Flex
            position="absolute"
            height="5rem"
            top="-1rem"
            bg="neutrals0"
            zIndex={-1}
            width={[
              'calc(100% + 2 * var(--space-7))',
              null,
              null,
              null,
              'calc(100% + 2 * var(--space-11))',
            ]}
            borderTop="1px solid var(--colors-neutrals40)"
          />
          <SelectedSlotsInfo {...infoProps} device={props.device} />
          <Button
            aria-label={null}
            analyticsName="cosmos-exclusives-reserve-a-tour-modal-request-times-button"
            onClick={props.onRequestTimeForTourClick}
            disabled={!canRequestTimeTour}
            width="100%"
            flex={1}
          >
            {canRequestTimeTour
              ? 'Request times for tour'
              : nSelectedSlots < 3
              ? 'Select at least 3 date/times'
              : 'Select at most 9 date/times'}
          </Button>
          <Flex
            position="absolute"
            height="1.5rem"
            bottom="-1.5rem"
            bg="neutrals0"
            width="100%"
            zIndex={-1}
          />
        </Flex>
      </Flex>
    </>
  );
}

const MotionText = motion(Text);

function Spot({
  id,
  slot,
  isDisabled,
  isSelected,
  isInstant,
  timezone = DEFAULT_TIMEZONE,
  onClick,
}: {
  id: string;
  slot: ExclusiveTourRequestTimeSlot;
  isDisabled?: boolean;
  isSelected?: boolean;
  isInstant?: boolean;
  onClick: (slot: ExclusiveTourRequestTimeSlot) => void;
  timezone: string | undefined;
}) {
  return (
    <Button
      id={id}
      disabled={isDisabled}
      textAlign="center"
      aria-label={null}
      analyticsName=""
      onClick={() => onClick(slot)}
      minWidth="120px"
      fontSize="s00"
      height="60px"
      paddingX="0.75rem"
      flexDirection="column"
      border="1px solid var(--colors-neutrals40) !important"
      variant="secondary"
      {...(isSelected && {
        color: 'neutrals0',
        bg: 'neutrals100',
        _hover: {
          opacity: 0.8,
        },
      })}
    >
      {moment(slot.start_time).tz(timezone).format('ha')} -{' '}
      {moment(slot.end_time).tz(timezone).format('ha')}
      {isPreferredExclusiveTourRequestTimeSlot(slot, timezone) && !isInstant && (
        <Text
          color={'neutrals70'}
          {...(!isSelected &&
            !isDisabled && {
              textColor: 'transparent',
              background: 'linear-gradient(56.35deg, #082DE6 36.59%, #0D76D6 103.23%)',
              backgroundClip: 'text',
            })}
        >
          Preferred
        </Text>
      )}
      {isInstant && (
        <Text
          color={!isSelected && !isDisabled ? 'green50' : 'neutrals70'}
          gridGap="0.125rem"
          display="inline-block"
        >
          <LightningIcon size="10" /> Instant
        </Text>
      )}
    </Button>
  );
}

const INFO_VARIANTS = {
  visible: { opacity: 1, height: 50 },
  hidden: { opacity: 0, height: 0 },
};

function SelectedSlotsInfo({
  hasInstantSpotSelected,
  hasNoPreferredOrInstantSlotsSelected,
  device: { isMobile, isSmallTablet },
}: {
  hasInstantSpotSelected: boolean;
  hasNoPreferredOrInstantSlotsSelected: boolean;
  device: ReturnType<typeof useDevice>;
}) {
  if (!isMobile && !isSmallTablet) {
    const isVisible = hasInstantSpotSelected || hasNoPreferredOrInstantSlotsSelected;
    return (
      <MotionText
        as="div"
        fontSize="s1"
        lineHeight="s00"
        display="flex"
        alignItems="center"
        textAlign="center"
        color="neutrals90"
        gridGap="0.25rem"
        flex={1}
        opacity={1}
        minHeight="22px"
        animate={
          isVisible ? omit(INFO_VARIANTS.visible, 'height') : omit(INFO_VARIANTS.hidden, 'height')
        }
      >
        {hasNoPreferredOrInstantSlotsSelected && (
          <>
            <Text
              as="span"
              textColor="transparent"
              background="linear-gradient(56.35deg, #082DE6 36.59%, #0D76D6 103.23%)"
              backgroundClip="text"
            >
              Preferred
            </Text>{' '}
            times are more likely to be accepted
          </>
        )}
        {hasInstantSpotSelected && (
          <>
            <LightningIcon color="var(--colors-green50)" />
            <Text as="span" color={'green50'}>
              Instant
            </Text>{' '}
            time confirms your tour immediately
          </>
        )}
      </MotionText>
    );
  }
  return (
    <AnimatePresence>
      {hasNoPreferredOrInstantSlotsSelected && (
        <MotionText
          color="neutrals90"
          fontSize="s0"
          lineHeight="s00"
          flex={1}
          opacity={1}
          variants={INFO_VARIANTS}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <Text
            as="span"
            textColor="transparent"
            background="linear-gradient(56.35deg, #082DE6 36.59%, #0D76D6 103.23%)"
            backgroundClip="text"
          >
            Preferred
          </Text>{' '}
          times are more likely to be accepted
        </MotionText>
      )}
      {hasInstantSpotSelected && (
        <MotionText
          as="div"
          fontSize="s0"
          textAlign="center"
          color="neutrals90"
          lineHeight="s00"
          display="flex"
          alignItems="center"
          flex={1}
          opacity={1}
          variants={INFO_VARIANTS}
          initial="hidden"
          animate="visible"
          exit="hidden"
          gridGap="0.25rem"
        >
          <LightningIcon color="var(--colors-green50)" />
          <Text as="span" color={'green50'}>
            Instant
          </Text>{' '}
          time confirms your tour immediately
        </MotionText>
      )}
    </AnimatePresence>
  );
}
